import { ModalConfirmation } from "@adsk/alloy-react-modal";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslations } from "@adsk/acc-issues-translations";
import { Issue } from "@adsk/acc-issues-types";
import { TypeDropdown } from "@adsk/acc-issues-common/components/input/TypeDropdown";
import {
  useDispatch,
  useSelector,
} from "@adsk/acc-issues-core/state/redux/hooks";
import { getIssueTypeEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueTypes";
import FormField from "@adsk/alloy-react-form-field";
import { Formik } from "formik";
import { restoreIssuesAction } from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from "@adsk/alloy-react-notification";

const initialValues = {
  issueTypeId: undefined,
};

const modalBodyId = "restore-deleted-issue-confirmation-modal-body";

export const RestoreDeletedIssueConfirmationModal = ({
  show,
  onConfirm,
  onClose,
  issuesToRestore,
  title,
  bodyText,
  onRestoreSuccess,
  onRestorePartialSuccess,
}: {
  show: boolean;
  onConfirm: () => void;
  onClose: () => void;
  issuesToRestore: Issue[];
  title: string;
  bodyText: string;
  onRestoreSuccess?: () => void;
  onRestorePartialSuccess?: ({
    failedIssueIds,
  }: {
    failedIssueIds: string[];
  }) => void;
}) => {
  const { T } = useTranslations();
  const typeEntities = useSelector(getIssueTypeEntitiesMap);
  const dispatch = useDispatch();
  const { showNotification } = useNotifications();

  // show to type dropdown only if the at least one issue previously had a type, but it no longer exists in typeEntities
  const shouldShowTypeDropdown = issuesToRestore.some(
    issue => !!issue.issueTypeId && !typeEntities[issue.issueTypeId]
  );

  const [modalBodyWidth, setModalBodyWidth] = useState(0);

  useEffect(() => {
    if (!show) {
      return;
    }
    setModalBodyWidth(document.getElementById(modalBodyId)?.offsetWidth ?? 0);
  }, [show]);

  const restoreIssues = useCallback(
    async ({ newIssueTypeId }: { newIssueTypeId?: string }) => {
      onConfirm();
      try {
        const result = await dispatch(
          restoreIssuesAction({
            issues: issuesToRestore,
            newIssueTypeId,
          })
        );
        // if there are errors, it was only a partial success.
        if (result.errors?.length) {
          onRestorePartialSuccess &&
            onRestorePartialSuccess({
              failedIssueIds: result.errors.map(
                (e: { issueId: string }) => e.issueId
              ),
            });
          showNotification({
            status: NOTIFICATION_STATUSES.SUCCESS,
            message: T.translate(
              "deletedIssues.restoredIssuesNotification.partialSuccess",
              {
                successfullyRestoredCount:
                  issuesToRestore.length - result.errors.length,
                totalCount: issuesToRestore.length,
              }
            ),
            dismissible: true,
          });
        }
        // successfully restored all the issues.
        else {
          const message =
            issuesToRestore.length > 1
              ? T.translate(
                  "deletedIssues.restoredIssuesNotification.bulkSuccess",
                  { numOfIssues: issuesToRestore.length }
                )
              : T.translate("deletedIssues.restoredIssuesNotification.success");
          showNotification({
            message,
            status: NOTIFICATION_STATUSES.SUCCESS,
            dismissible: true,
          });
          onRestoreSuccess && onRestoreSuccess();
        }
      } catch (e) {
        const message =
          issuesToRestore.length > 1
            ? T.translate(
                "deletedIssues.restoredIssuesNotification.bulkFailure"
              )
            : T.translate("deletedIssues.restoredIssuesNotification.failure");
        showNotification({
          message,
          status: NOTIFICATION_STATUSES.ERROR,
          dismissible: true,
        });
      }
    },
    [
      T,
      dispatch,
      issuesToRestore,
      onConfirm,
      onRestoreSuccess,
      showNotification,
    ]
  );

  const onValidFormSubmit = useCallback(
    async (values, { resetForm }) => {
      restoreIssues({ newIssueTypeId: values.issueTypeId });
      resetForm();
    },
    [restoreIssues]
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onValidFormSubmit}
      validateOnChange={true}
      validate={values => {
        const errors = {} as any;
        if (shouldShowTypeDropdown && values.issueTypeId == undefined) {
          errors.issueTypeId = T.translate("validation.required");
        }
        return errors;
      }}
    >
      {({ handleSubmit, values, setFieldValue, errors }) => {
        return (
          <div style={{ position: "relative" }}>
            <ModalConfirmation
              style={{}}
              show={show}
              title={title}
              message={
                <div
                  id={modalBodyId}
                  style={{ paddingBottom: shouldShowTypeDropdown ? 36 : 0 }}
                >
                  <p>{bodyText}</p>
                  {shouldShowTypeDropdown && (
                    <>
                      <p>
                        {T.translate(
                          "deletedIssues.restoreModal.noTypeAdditionalMessage"
                        )}
                      </p>
                      <FormField
                        style={{
                          position: "absolute",
                          width: modalBodyWidth,
                        }}
                        error={errors.issueTypeId}
                      >
                        <TypeDropdown
                          selectedTypeId={values.issueTypeId}
                          onChange={typeId => {
                            setFieldValue("issueTypeId", typeId);
                          }}
                          onClear={() => {
                            setFieldValue("issueTypeId", undefined);
                          }}
                          isClearable
                        />
                      </FormField>
                    </>
                  )}
                </div>
              }
              onConfirm={() => {
                handleSubmit();
              }}
              onClose={onClose}
              confirmationButtonLabel={
                T.translate(
                  "deletedIssues.restoreModal.restoreButton"
                ) as string
              }
              isConfirmationButtonAlert={false}
            ></ModalConfirmation>
          </div>
        );
      }}
    </Formik>
  );
};
